/**
 * DatabaseSearchSelect renderer — search a collection and pick one record.
 * Phase 0: uses the existing /api/search endpoint if available; falls back
 * to a raw text input to capture the picked record's id/name.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Search, Loader2 } from 'lucide-react';

const api = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api` });
api.interceptors.request.use(cfg => {
  const t = localStorage.getItem('access_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export default function DatabaseSearchSelect({ action, value, onChange, onNext, onBack, canBack }) {
  const params = action.params || {};
  const collection = params.collection || 'clients';
  const displayFields = (params.display_fields || 'full_name,phone').split(',').map(s => s.trim());
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [picked, setPicked] = useState(value || null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { setPicked(value || null); setQuery(''); setResults([]); }, [action.id]);   // eslint-disable-line react-hooks/exhaustive-deps

  const doSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      // Try the generic /search endpoint first
      const res = await api.get('/search', { params: { q: query, kind: collection } });
      setResults(Array.isArray(res.data) ? res.data.slice(0, 8) : (res.data.items || []).slice(0, 8));
    } catch (e) {
      // Fall back to direct collection search
      try {
        const res = await api.get(`/${collection}`, { params: { q: query, limit: 8 } });
        setResults((res.data || []).slice(0, 8));
      } catch (e2) {
        setResults([]);
      }
    } finally { setLoading(false); }
  };

  const pick = (r) => {
    const id = r.id || r._id || r.appraisal_id || null;
    const label = displayFields.map(f => r[f]).filter(Boolean).join(' · ') || id;
    const val = { id, label, record: r };
    setPicked(val);
    onChange(val);
  };

  return (
    <div>
      <div className="bg-gradient-to-br from-indigo-50 to-white border-l-4 border-indigo-400 rounded-r-lg p-6 mb-6">
        <div className="text-[10px] uppercase tracking-widest text-indigo-600 font-semibold mb-2">🔍 Look up record</div>
        <div className="text-lg text-slate-800">Search <code className="text-sm bg-indigo-50 px-1.5 rounded">{collection}</code> and pick the matching record.</div>
      </div>

      <div className="flex gap-2 mb-4">
        <input value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') doSearch(); }}
          data-testid="wf-search-input"
          placeholder={`Search by ${(params.search_fields || 'name').split(',').join(', ')}…`}
          className="flex-1 text-base px-4 py-3 border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-300" />
        <button onClick={doSearch} disabled={loading || !query.trim()}
          data-testid="wf-search-btn"
          className="px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Search
        </button>
      </div>

      {results.length > 0 && (
        <div className="border border-slate-200 rounded mb-4 divide-y divide-slate-100 bg-white max-h-64 overflow-y-auto">
          {results.map((r, i) => {
            const id = r.id || r._id || i;
            const isPicked = picked && picked.id === id;
            return (
              <button key={id} onClick={() => pick(r)}
                data-testid={`wf-search-result-${i}`}
                className={`w-full text-left px-4 py-2 hover:bg-indigo-50 ${isPicked ? 'bg-indigo-100' : ''}`}>
                {displayFields.map(f => (
                  <span key={f} className="mr-3 text-sm">
                    <span className="text-slate-400 text-xs">{f}:</span> <span className="text-slate-700">{r[f] || '—'}</span>
                  </span>
                ))}
              </button>
            );
          })}
        </div>
      )}

      {picked && (
        <div className="p-3 bg-emerald-50 border border-emerald-200 rounded mb-4 text-sm text-emerald-800">
          ✓ Selected: <b>{picked.label}</b>
        </div>
      )}

      <div className="flex items-center gap-3">
        {canBack && <button onClick={onBack} data-testid="wf-back-btn"
          className="px-4 py-2 text-slate-500 hover:text-slate-700">← Back</button>}
        <span className="flex-1" />
        <button onClick={onNext} disabled={!picked}
          data-testid="wf-next-btn"
          className="px-6 py-2 bg-sky-600 text-white font-semibold rounded hover:bg-sky-700 disabled:opacity-40">Next →</button>
      </div>
    </div>
  );
}
